import React, { useState, useEffect, useRef } from 'react';
import c2Service from '../../services/c2Service';
import { useTheme } from '../../contexts/ThemeContext';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faBox, faDesktop, faCopy, faQuestionCircle, faInfoCircle, faRoute } from '@fortawesome/free-solid-svg-icons';
import { faApple, faWindows, faLinux } from '@fortawesome/free-brands-svg-icons';
import { OverlayTrigger, Tooltip, Modal } from 'react-bootstrap';
import RouteBuilder from './RouteBuilder';

// Help tooltip component
const HelpTooltip = ({ text, children }) => {
  const { darkMode } = useTheme();
  return (
    <OverlayTrigger
      placement="top"
      overlay={
        <Tooltip>
          <div style={{ textAlign: 'left', padding: '4px' }}>{text}</div>
        </Tooltip>
      }
    >
      <span style={{ cursor: 'help', marginLeft: '6px', color: darkMode ? '#9ca3af' : '#6b7280' }}>
        {children || <FontAwesomeIcon icon={faQuestionCircle} />}
      </span>
    </OverlayTrigger>
  );
};

const StagerBuilder = () => {
  const { darkMode } = useTheme();
  const buildTimersRef = useRef([]);
  const [payloads, setPayloads] = useState([]);
  const [agentBuilds, setAgentBuilds] = useState([]);
  const [loading, setLoading] = useState(true);
  const [payloadDir, setPayloadDir] = useState('');
  const [xorKeySet, setXorKeySet] = useState(false);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [uploadFile, setUploadFile] = useState(null);
  const [uploadPlatform, setUploadPlatform] = useState('windows');
  const [uploadArch, setUploadArch] = useState('amd64');
  const [uploadVariant, setUploadVariant] = useState('full');
  const [showBuildModal, setShowBuildModal] = useState(false);
  const [buildPlatform, setBuildPlatform] = useState('all');
  const [building, setBuilding] = useState(false);
  const [buildOutput, setBuildOutput] = useState('');
  const [showRouteBuilder, setShowRouteBuilder] = useState(false);
  const [selectedRoute, setSelectedRoute] = useState(null);
  const [buildConfig, setBuildConfig] = useState({
    name: '',
    comment: '',
    c2Url: 'https://app.purplehq.ch',
    c2Port: 443,
    protocol: 'https',
    sleepInterval: 60,
    jitter: 30,
    userAgent: 'Mozilla/5.0 (Windows NT 10.0; Win64; x64) AppleWebKit/537.36',
    killDate: '',
    encryption: 'aes256',
    obfuscation: true,
    antiDebug: true,
    sandbox: true,
    // Staged payload options
    isStaged: true,
    stagerType: 'exe',
    stageProtocol: 'https',
    stageEncryption: 'chacha20',
    // Modular capabilities (build tags — controls what's compiled into the binary)
    modEvasion: false,
    modCredentials: false,
    modLateral: false,
    modInjection: false,
    modKeylogger: false,
    // Evasion options (runtime behavior, separate from whether the module is compiled)
    autoAMSI: false,
    autoETW: false,
    injectionMethod: 'createRemoteThread',
    sleepObfuscation: false,
    syscalls: false,
    // Working hours
    workingHoursEnabled: false,
    workingHoursStart: 9,
    workingHoursEnd: 17,
    // Route configuration
    routeConfig: null
  });

  useEffect(() => {
    fetchPayloads();
    fetchAgentBuilds();
    return () => {
      buildTimersRef.current.forEach(id => clearTimeout(id));
      buildTimersRef.current.forEach(id => clearInterval(id));
    };
  }, []);

  const fetchPayloads = async () => {
    try {
      setLoading(true);
      const response = await c2Service.getStageInfo();
      setPayloads(response.data?.payloads || []);
      setPayloadDir(response.data?.payloadDir || '');
      setXorKeySet(response.data?.xorKeySet || false);
    } catch (error) {
      console.error('Error fetching payloads:', error);
      // If endpoint doesn't exist yet, show placeholder
      setPayloads([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchAgentBuilds = async () => {
    try {
      const response = await c2Service.getAgentBuilds();
      setAgentBuilds(response.data?.builds || []);
    } catch (error) {
      console.error('Error fetching agent builds:', error);
      setAgentBuilds([]);
    }
  };

  const handleDownloadStager = async (platform, arch, variant) => {
    try {
      await c2Service.downloadStagePayload(platform, arch, variant);
    } catch (error) {
      console.error('Error downloading stager:', error);
      alert('Failed to download stager');
    }
  };

  const handleUploadPayload = async () => {
    if (!uploadFile) {
      alert('Please select a file to upload');
      return;
    }

    try {
      const response = await c2Service.uploadStagePayload(
        uploadPlatform,
        uploadArch,
        uploadVariant,
        uploadFile
      );

      if (response.data?.success) {
        alert('Payload uploaded successfully!');
        setShowUploadModal(false);
        setUploadFile(null);
        await fetchPayloads();
      }
    } catch (error) {
      console.error('Error uploading payload:', error);
      alert(error.response?.data?.error || 'Failed to upload payload');
    }
  };

  const handleBuildPayloads = async () => {
    try {
      setBuilding(true);
      setBuildOutput('Starting agent build...\n');

      const response = await c2Service.buildPayloads(buildPlatform, 'all', buildConfig);

      if (response.data?.success) {
        setBuildOutput(prev => prev + '\nBuild started: ' + response.data.build.name + '\n\nWaiting for build to complete...\n');

        // Poll for build completion
        let attempts = 0;
        const pollInterval = setInterval(async () => {
          attempts++;
          try {
            await fetchAgentBuilds();

            if (attempts > 20) {
              clearInterval(pollInterval);
              setBuildOutput(prev => prev + '\nBuild is taking longer than expected. Check the Agent Builds list below.\n');
              setBuilding(false);
              const t = setTimeout(() => {
                setShowBuildModal(false);
                setBuildOutput('');
              }, 3000);
              buildTimersRef.current.push(t);
            }
          } catch (err) {
            console.error('Poll error:', err);
          }
        }, 1000);
        buildTimersRef.current.push(pollInterval);

        const outerTimeout = setTimeout(() => {
          clearInterval(pollInterval);
          setBuildOutput(prev => prev + '\nBuild processing completed. Check Agent Builds below.\n');
          setBuilding(false);
          const closeTimeout = setTimeout(() => {
            setShowBuildModal(false);
            setBuildOutput('');
            // Reset form
            setBuildConfig({
              name: '',
              comment: '',
              c2Url: 'https://app.purplehq.ch',
              c2Port: 443,
              protocol: 'https',
              sleepInterval: 60,
              jitter: 30,
              userAgent: 'Mozilla/5.0 (Windows NT 10.0; Win64; x64) AppleWebKit/537.36',
              killDate: '',
              encryption: 'aes256',
              obfuscation: true,
              antiDebug: true,
              sandbox: true,
              isStaged: true,
              stagerType: 'exe',
              stageProtocol: 'https',
              stageEncryption: 'chacha20',
              autoAMSI: false,
              autoETW: false,
              injectionMethod: 'createRemoteThread',
              sleepObfuscation: false,
              syscalls: false,
              workingHoursEnabled: false,
              workingHoursStart: 9,
              workingHoursEnd: 17
            });
          }, 2000);
          buildTimersRef.current.push(closeTimeout);
        }, 5000);
        buildTimersRef.current.push(outerTimeout);
      } else {
        setBuildOutput(prev => prev + '\nBuild failed:\n' + (response.data?.error || 'Unknown error'));
        setBuilding(false);
      }
    } catch (error) {
      console.error('Error building payloads:', error);
      setBuildOutput(prev => prev + '\nBuild failed:\n' + (error.response?.data?.error || error.message));
      setBuilding(false);
    }
  };

  const getPlatformIcon = (platform) => {
    const icons = {
      windows: faWindows,
      linux: faLinux,
      darwin: faApple,
      macos: faApple
    };
    return icons[platform] || faDesktop;
  };

  const getPlatformColor = (platform) => {
    const colors = {
      windows: 'bg-blue-500',
      linux: 'bg-orange-500',
      darwin: 'bg-gray-500',
      macos: 'bg-gray-500'
    };
    return colors[platform] || 'bg-gray-400';
  };

  const handleDownloadArtifact = async (buildId, filename) => {
    try {
      await c2Service.downloadAgentBuildArtifact(buildId, filename);
    } catch (error) {
      console.error('Error downloading artifact:', error);
      alert('Failed to download artifact: ' + (error.response?.data?.error || error.message));
    }
  };

  const handleDeleteBuild = async (buildId, buildName) => {
    if (!window.confirm(`Delete agent build "${buildName}"? This will remove all artifacts and cannot be undone.`)) {
      return;
    }

    try {
      await c2Service.deleteAgentBuild(buildId);
      alert('Build deleted successfully');
      await fetchAgentBuilds();
    } catch (error) {
      console.error('Error deleting build:', error);
      alert('Failed to delete build: ' + (error.response?.data?.error || error.message));
    }
  };

  const getStatusBadge = (status) => {
    const badges = {
      pending: { bg: 'bg-gray-500', text: 'Pending' },
      building: { bg: 'bg-blue-500 animate-pulse', text: 'Building' },
      success: { bg: 'bg-green-500', text: 'Success' },
      failed: { bg: 'bg-red-500', text: 'Failed' }
    };
    const badge = badges[status] || badges.pending;
    return (
      <span className={`text-xs px-2 py-1 rounded text-white ${badge.bg}`}>
        {badge.text}
      </span>
    );
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className={`p-6 rounded-lg ${darkMode ? 'bg-gray-800' : 'bg-white'}`}>
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className={`text-2xl font-bold ${darkMode ? 'text-white' : 'text-gray-900'}`}>
            Staged Payload Manager
          </h2>
          <p className={`text-sm mt-1 ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
            Manage stagers and full agent payloads for staged deployment
          </p>
        </div>
        <div className="flex gap-2">
          <button
            onClick={() => setShowBuildModal(true)}
            className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors flex items-center gap-2"
          >
            <FontAwesomeIcon icon={faBox} />
            Build Payloads
          </button>
          <button
            onClick={() => setShowUploadModal(true)}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center gap-2"
          >
            <FontAwesomeIcon icon={faBox} />
            Upload Payload
          </button>
        </div>
      </div>

      {/* Status Banner */}
      <div className={`mb-6 p-4 rounded-lg border ${darkMode ? 'bg-gray-900 border-gray-700' : 'bg-gray-50 border-gray-200'}`}>
        <div className="flex items-center justify-between">
          <div>
            <div className={`text-sm font-medium ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
              Payload Directory
            </div>
            <div className={`text-xs mt-1 font-mono ${darkMode ? 'text-gray-500' : 'text-gray-500'}`}>
              {payloadDir || 'Not configured'}
            </div>
          </div>
          <div>
            <div className={`text-sm font-medium ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
              XOR Obfuscation
            </div>
            <div className="flex items-center gap-2 mt-1">
              <div className={`w-2 h-2 rounded-full ${xorKeySet ? 'bg-green-500' : 'bg-red-500'}`}></div>
              <span className={`text-xs ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                {xorKeySet ? 'Enabled' : 'Disabled'}
              </span>
            </div>
          </div>
          <div>
            <div className={`text-sm font-medium ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
              Available Payloads
            </div>
            <div className={`text-xs mt-1 ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
              {payloads.length} file(s)
            </div>
          </div>
        </div>
      </div>

      {/* How It Works */}
      <div className={`mb-6 p-4 rounded-lg border ${darkMode ? 'bg-blue-900/20 border-blue-800' : 'bg-blue-50 border-blue-200'}`}>
        <h3 className={`text-sm font-semibold mb-2 ${darkMode ? 'text-blue-300' : 'text-blue-800'}`}>
          How Staged Payloads Work
        </h3>
        <ol className={`text-sm space-y-1 list-decimal list-inside ${darkMode ? 'text-blue-400' : 'text-blue-700'}`}>
          <li>Small <strong>stager</strong> (~5KB) is deployed to target system</li>
          <li>Stager downloads XOR-encoded <strong>full agent</strong> from C2 server</li>
          <li>Agent is decoded, verified (SHA256), and executed in-memory (Windows) or via temp file (Linux/macOS)</li>
          <li>Full agent establishes C2 connection with all capabilities</li>
        </ol>
      </div>

      {/* Payload Grid */}
      {payloads.length === 0 ? (
        <div className={`text-center py-12 rounded-lg border-2 border-dashed ${darkMode ? 'border-gray-700 text-gray-400' : 'border-gray-200 text-gray-500'}`}>
          <p className="text-lg mb-2">No payloads available</p>
          <p className="text-sm">Build agents with: <code className="font-mono bg-gray-200 dark:bg-gray-700 px-2 py-1 rounded">make payloads</code></p>
          <p className="text-sm mt-1">Then upload them here or copy to: <code className="font-mono bg-gray-200 dark:bg-gray-700 px-2 py-1 rounded">{payloadDir}</code></p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {payloads.map((payload, index) => (
            <div
              key={index}
              className={`p-4 rounded-lg border ${darkMode ? 'bg-gray-900 border-gray-700' : 'bg-gray-50 border-gray-200'}`}
            >
              {/* Platform Badge */}
              <div className="flex items-center gap-2 mb-3">
                <span className="text-2xl">
                  <FontAwesomeIcon icon={getPlatformIcon(payload.platform)} />
                </span>
                <div className="flex-1">
                  <div className={`text-sm font-semibold ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                    {payload.platform.charAt(0).toUpperCase() + payload.platform.slice(1)}
                  </div>
                  <div className={`text-xs ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                    {payload.arch} • {payload.variant}
                  </div>
                </div>
                <span className={`text-xs px-2 py-1 rounded text-white ${getPlatformColor(payload.platform)}`}>
                  {payload.arch}
                </span>
              </div>

              {/* Details */}
              <div className="space-y-2 mb-4">
                <div className="flex items-center justify-between text-xs">
                  <span className={darkMode ? 'text-gray-500' : 'text-gray-500'}>Size</span>
                  <span className={`font-mono ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                    {payload.sizeHuman}
                  </span>
                </div>
                <div className="flex items-center justify-between text-xs">
                  <span className={darkMode ? 'text-gray-500' : 'text-gray-500'}>Hash</span>
                  <span className={`font-mono ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                    {payload.hash}
                  </span>
                </div>
                <div className="flex items-center justify-between text-xs">
                  <span className={darkMode ? 'text-gray-500' : 'text-gray-500'}>Modified</span>
                  <span className={darkMode ? 'text-gray-300' : 'text-gray-700'}>
                    {new Date(payload.modified).toLocaleDateString()}
                  </span>
                </div>
                <div className="flex items-center justify-between text-xs">
                  <span className={darkMode ? 'text-gray-500' : 'text-gray-500'}>Filename</span>
                  <span className={`font-mono text-xs ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                    {payload.filename}
                  </span>
                </div>
              </div>

              {/* Actions — this is the STAGER endpoint (XOR-encoded, not directly executable) */}
              <div className="flex flex-col gap-1">
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => handleDownloadStager(payload.platform, payload.arch, payload.variant)}
                    className={`flex-1 px-3 py-2 text-sm rounded ${darkMode ? 'bg-amber-900 text-amber-300 hover:bg-amber-800' : 'bg-amber-100 text-amber-700 hover:bg-amber-200'}`}
                    title="Downloads XOR-encoded payload for stagers — NOT directly executable! For a runnable .exe, use Agent Builds below."
                  >
                    ⚡ Download (XOR-encoded for stager)
                  </button>
                  <button
                    className={`px-3 py-2 text-sm rounded ${darkMode ? 'bg-gray-700 text-gray-300 hover:bg-gray-600' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`}
                    title="Copy stager download URL (for stager payload retrieval)"
                    onClick={() => {
                      const url = `${window.location.origin}/api/c2/stage/download?platform=${payload.platform}&arch=${payload.arch}&variant=${payload.variant}`;
                      navigator.clipboard.writeText(url);
                      alert('Stager download URL copied to clipboard!\n\n⚠️ This URL serves XOR-encoded payloads for stagers, not raw executables.');
                    }}
                  >
                    <FontAwesomeIcon icon={faCopy} />
                  </button>
                </div>
                <p className={`text-[10px] ${darkMode ? 'text-amber-400/60' : 'text-amber-600/60'}`}>
                  ⚠️ XOR-encoded — for stager use only. For a runnable .exe, download from <strong>Agent Builds</strong> below.
                </p>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Build Instructions */}
      <div className={`mt-6 p-4 rounded-lg border ${darkMode ? 'bg-gray-900 border-gray-700' : 'bg-gray-50 border-gray-200'}`}>
        <h3 className={`text-sm font-semibold mb-3 ${darkMode ? 'text-white' : 'text-gray-900'}`}>
          Building Payloads
        </h3>

        <div className="space-y-3">
          <div>
            <div className={`text-xs font-medium mb-1 ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
              Option A: Automated Build (Recommended)
            </div>
            <pre className={`text-xs p-2 rounded font-mono overflow-x-auto ${darkMode ? 'bg-gray-800 text-gray-300' : 'bg-gray-100 text-gray-700'}`}>
cd backend/scripts{'\n'}
node buildStager.js --c2-url https://app.purplehq.ch --platform all --deploy
            </pre>
          </div>

          <div>
            <div className={`text-xs font-medium mb-1 ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
              Option B: Manual Build
            </div>
            <pre className={`text-xs p-2 rounded font-mono overflow-x-auto ${darkMode ? 'bg-gray-800 text-gray-300' : 'bg-gray-100 text-gray-700'}`}>
cd agent{'\n'}
make stager    # Build stager (~5KB){'\n'}
make payloads  # Build full agents (all platforms)
            </pre>
          </div>

          <div>
            <div className={`text-xs font-medium mb-1 ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
              Deploy to Server
            </div>
            <pre className={`text-xs p-2 rounded font-mono overflow-x-auto ${darkMode ? 'bg-gray-800 text-gray-300' : 'bg-gray-100 text-gray-700'}`}>
scp backend/payloads/*.bin root@185.145.148.203:/opt/purpleteam/backend/payloads/
            </pre>
          </div>
        </div>
      </div>

      {/* Agent Builds Section */}
      <div className="mt-8">
        <h3 className={`text-xl font-bold mb-4 ${darkMode ? 'text-white' : 'text-gray-900'}`}>
          Agent Builds
        </h3>

        {agentBuilds.length === 0 ? (
          <div className={`text-center py-8 rounded-lg border-2 border-dashed ${darkMode ? 'border-gray-700 text-gray-400' : 'border-gray-200 text-gray-500'}`}>
            <p className="text-sm mb-2">No agent builds yet</p>
            <p className="text-xs">Click "Build Payloads" to create your first custom agent build</p>
          </div>
        ) : (
          <div className="space-y-4">
            {agentBuilds.map((build) => (
              <div
                key={build._id}
                className={`p-4 rounded-lg border ${darkMode ? 'bg-gray-900 border-gray-700' : 'bg-gray-50 border-gray-200'}`}
              >
                <div className="flex items-start justify-between mb-3">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-1">
                      <h4 className={`text-lg font-semibold ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                        {build.name}
                      </h4>
                      {getStatusBadge(build.status)}
                      <span className={`text-xs px-2 py-1 rounded ${darkMode ? 'bg-gray-800 text-gray-400' : 'bg-gray-100 text-gray-600'}`}>
                        {build.platform === 'all' ? 'All Platforms' : build.platform}
                      </span>
                    </div>
                    {build.comment && (
                      <p className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                        {build.comment}
                      </p>
                    )}
                    <div className={`text-xs mt-2 ${darkMode ? 'text-gray-500' : 'text-gray-500'}`}>
                      Created: {new Date(build.createdAt).toLocaleString()} •
                      By: {build.createdBy?.username || 'Unknown'}
                    </div>
                    {build.config && (
                      <div className={`flex flex-wrap gap-x-3 gap-y-1 mt-2 text-xs ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                        <span>C2: <span className="font-mono">{build.config.c2Url}:{build.config.c2Port}</span></span>
                        <span>Protocol: {build.config.protocol}</span>
                        <span>Sleep: {build.config.sleepInterval}s ± {build.config.jitter}%</span>
                        <span>Encryption: {build.config.encryption}</span>
                        {build.config.killDate && <span>Kill: {new Date(build.config.killDate).toLocaleDateString()}</span>}
                        {build.config.obfuscation && <span className={`px-1 rounded ${darkMode ? 'bg-green-900/30 text-green-400' : 'bg-green-100 text-green-700'}`}>Obfuscation</span>}
                        {build.config.antiDebug && <span className={`px-1 rounded ${darkMode ? 'bg-green-900/30 text-green-400' : 'bg-green-100 text-green-700'}`}>Anti-Debug</span>}
                        {build.config.sandbox && <span className={`px-1 rounded ${darkMode ? 'bg-green-900/30 text-green-400' : 'bg-green-100 text-green-700'}`}>Sandbox</span>}
                        {build.config.isStaged && <span className={`px-1 rounded ${darkMode ? 'bg-blue-900/30 text-blue-400' : 'bg-blue-100 text-blue-700'}`}>Staged ({build.config.stagerType})</span>}
                        {build.config.autoAMSI && <span className={`px-1 rounded ${darkMode ? 'bg-purple-900/30 text-purple-400' : 'bg-purple-100 text-purple-700'}`}>AMSI Bypass</span>}
                        {build.config.autoETW && <span className={`px-1 rounded ${darkMode ? 'bg-purple-900/30 text-purple-400' : 'bg-purple-100 text-purple-700'}`}>ETW Bypass</span>}
                        {build.config.sleepObfuscation && <span className={`px-1 rounded ${darkMode ? 'bg-purple-900/30 text-purple-400' : 'bg-purple-100 text-purple-700'}`}>Sleep Obf</span>}
                        {build.config.syscalls && <span className={`px-1 rounded ${darkMode ? 'bg-purple-900/30 text-purple-400' : 'bg-purple-100 text-purple-700'}`}>Syscalls</span>}
                        {build.config.injectionMethod && build.config.injectionMethod !== 'createRemoteThread' && <span>Injection: {build.config.injectionMethod}</span>}
                        {build.config.workingHoursEnabled && <span>Hours: {build.config.workingHoursStart}-{build.config.workingHoursEnd}</span>}
                      </div>
                    )}
                  </div>
                  <button
                    onClick={() => handleDeleteBuild(build._id, build.name)}
                    className={`px-3 py-1 text-xs rounded ${darkMode ? 'bg-red-900 text-red-300 hover:bg-red-800' : 'bg-red-100 text-red-700 hover:bg-red-200'}`}
                  >
                    Delete
                  </button>
                </div>

                {build.status === 'building' && (
                  <div className="flex items-center gap-2 mb-3">
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-500"></div>
                    <span className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                      Building agents... This may take 1-2 minutes.
                    </span>
                  </div>
                )}

                {build.status === 'failed' && build.buildError && (
                  <div className={`p-3 mb-3 rounded ${darkMode ? 'bg-red-900/20 border border-red-800' : 'bg-red-50 border border-red-200'}`}>
                    <p className={`text-sm font-medium mb-1 ${darkMode ? 'text-red-400' : 'text-red-700'}`}>
                      Build Failed:
                    </p>
                    <pre className={`text-xs font-mono ${darkMode ? 'text-red-300' : 'text-red-600'}`}>
                      {build.buildError}
                    </pre>
                  </div>
                )}

                {build.status === 'success' && build.artifacts && build.artifacts.length > 0 && (
                  <div>
                    <div className={`text-sm font-medium mb-2 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                      Available Artifacts ({build.artifacts.length}):
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
                      {build.artifacts.map((artifact, idx) => (
                        <div
                          key={idx}
                          className={`p-3 rounded border ${darkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-300'}`}
                        >
                          <div className="flex items-center gap-2 mb-2">
                            <FontAwesomeIcon
                              icon={getPlatformIcon(artifact.platform)}
                              className={darkMode ? 'text-gray-400' : 'text-gray-600'}
                            />
                            <div className="flex-1">
                              <div className={`text-sm font-medium ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                                {artifact.platform.charAt(0).toUpperCase() + artifact.platform.slice(1)}
                              </div>
                              <div className={`text-xs ${darkMode ? 'text-gray-500' : 'text-gray-600'}`}>
                                {artifact.arch}
                              </div>
                            </div>
                          </div>
                          <div className={`text-xs mb-2 ${darkMode ? 'text-gray-500' : 'text-gray-600'}`}>
                            <div>Size: {(artifact.size / 1024 / 1024).toFixed(2)} MB</div>
                            <div className="font-mono text-xs truncate">
                              SHA256: {artifact.sha256?.substring(0, 12)}...
                            </div>
                          </div>
                          <button
                            onClick={() => handleDownloadArtifact(build._id, artifact.filename)}
                            className={`w-full px-2 py-1.5 text-xs font-medium rounded ${darkMode ? 'bg-green-800 text-green-200 hover:bg-green-700' : 'bg-green-200 text-green-800 hover:bg-green-300'}`}
                            title="Download raw executable — ready to run"
                          >
                            ⬇ Download .exe
                          </button>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {build.status === 'success' && (!build.artifacts || build.artifacts.length === 0) && (
                  <div className={`text-sm ${darkMode ? 'text-gray-500' : 'text-gray-600'}`}>
                    No artifacts available
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Upload Modal */}
      {showUploadModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className={`max-w-lg w-full rounded-lg ${darkMode ? 'bg-gray-800' : 'bg-white'} p-6`}>
            <div className="flex items-center justify-between mb-6">
              <h3 className={`text-xl font-bold ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                Upload Agent Payload
              </h3>
              <button
                onClick={() => setShowUploadModal(false)}
                className={`text-2xl ${darkMode ? 'text-gray-400 hover:text-white' : 'text-gray-500 hover:text-gray-700'}`}
              >
                ×
              </button>
            </div>

            <div className="space-y-4">
              <div>
                <label className={`block text-sm font-medium mb-2 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                  Platform
                </label>
                <select
                  value={uploadPlatform}
                  onChange={(e) => setUploadPlatform(e.target.value)}
                  className={`w-full px-3 py-2 border rounded-lg ${darkMode ? 'bg-gray-700 border-gray-600 text-white' : 'border-gray-300'}`}
                >
                  <option value="windows">Windows</option>
                  <option value="linux">Linux</option>
                  <option value="darwin">macOS</option>
                </select>
              </div>

              <div>
                <label className={`block text-sm font-medium mb-2 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                  Architecture
                </label>
                <select
                  value={uploadArch}
                  onChange={(e) => setUploadArch(e.target.value)}
                  className={`w-full px-3 py-2 border rounded-lg ${darkMode ? 'bg-gray-700 border-gray-600 text-white' : 'border-gray-300'}`}
                >
                  <option value="amd64">amd64 (x86_64)</option>
                  <option value="386">386 (x86)</option>
                  <option value="arm64">arm64</option>
                </select>
              </div>

              <div>
                <label className={`block text-sm font-medium mb-2 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                  Variant
                </label>
                <select
                  value={uploadVariant}
                  onChange={(e) => setUploadVariant(e.target.value)}
                  className={`w-full px-3 py-2 border rounded-lg ${darkMode ? 'bg-gray-700 border-gray-600 text-white' : 'border-gray-300'}`}
                >
                  <option value="full">Full (with evasion)</option>
                  <option value="minimal">Minimal</option>
                  <option value="evasion">Evasion only</option>
                </select>
              </div>

              <div>
                <label className={`block text-sm font-medium mb-2 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                  Agent Binary
                </label>
                <input
                  type="file"
                  onChange={(e) => setUploadFile(e.target.files[0])}
                  className={`w-full px-3 py-2 border rounded-lg ${darkMode ? 'bg-gray-700 border-gray-600 text-white' : 'border-gray-300'}`}
                />
                <p className={`text-xs mt-1 ${darkMode ? 'text-gray-500' : 'text-gray-500'}`}>
                  Will be saved as: agent-{uploadPlatform}-{uploadArch}-{uploadVariant}.bin
                </p>
              </div>
            </div>

            <div className="flex items-center justify-end gap-3 mt-6">
              <button
                onClick={() => setShowUploadModal(false)}
                className={`px-4 py-2 rounded ${darkMode ? 'bg-gray-700 text-gray-300 hover:bg-gray-600' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`}
              >
                Cancel
              </button>
              <button
                onClick={handleUploadPayload}
                className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
              >
                Upload
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Build Modal */}
      {showBuildModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className={`max-w-lg w-full rounded-lg ${darkMode ? 'bg-gray-800' : 'bg-white'} p-6`}>
            <div className="flex items-center justify-between mb-6">
              <h3 className={`text-xl font-bold ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                <FontAwesomeIcon icon={faBox} className="me-2" />
                Build Agent Payloads
              </h3>
              <button
                onClick={() => setShowBuildModal(false)}
                disabled={building}
                className={`text-2xl ${darkMode ? 'text-gray-400 hover:text-white' : 'text-gray-500 hover:text-gray-700'} ${building ? 'opacity-50 cursor-not-allowed' : ''}`}
              >
                ×
              </button>
            </div>

            <div className="space-y-4 max-h-[70vh] overflow-y-auto">
              {/* Agent Identification */}
              <div className={`p-3 rounded ${darkMode ? 'bg-gray-900 border border-gray-700' : 'bg-gray-50 border border-gray-200'}`}>
                <h4 className={`text-sm font-semibold mb-3 ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                  🏷️ Agent Identification
                  <HelpTooltip text="Give your agent build a unique name to track which agents belong to which campaign or client">
                    <FontAwesomeIcon icon={faInfoCircle} style={{ fontSize: '14px' }} />
                  </HelpTooltip>
                </h4>
                <div className="space-y-3">
                  <div>
                    <label className={`block text-xs font-medium mb-1 ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                      Agent Name <span className="text-red-500">*</span>
                      <HelpTooltip text="Use a descriptive name like 'client-campaign-date'. This helps identify agents in the beacon list." />
                    </label>
                    <input
                      type="text"
                      value={buildConfig.name}
                      onChange={(e) => setBuildConfig({ ...buildConfig, name: e.target.value })}
                      disabled={building}
                      placeholder="e.g., acme-corp-phishing-2024Q2"
                      className={`w-full px-3 py-2 border rounded text-sm ${darkMode ? 'bg-gray-800 border-gray-600 text-white' : 'border-gray-300'}`}
                    />
                    <p className={`text-xs mt-1 ${darkMode ? 'text-gray-500' : 'text-gray-500'}`}>
                      <FontAwesomeIcon icon={faInfoCircle} /> Must be unique across all builds
                    </p>
                  </div>
                  <div>
                    <label className={`block text-xs font-medium mb-1 ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                      Comment (Optional)
                      <HelpTooltip text="Add notes about this build's purpose, target, or special configuration" />
                    </label>
                    <textarea
                      value={buildConfig.comment}
                      onChange={(e) => setBuildConfig({ ...buildConfig, comment: e.target.value })}
                      disabled={building}
                      placeholder="e.g., Red team exercise - finance department social engineering test"
                      rows={2}
                      className={`w-full px-3 py-2 border rounded text-sm ${darkMode ? 'bg-gray-800 border-gray-600 text-white' : 'border-gray-300'}`}
                    />
                  </div>
                </div>
              </div>

              {/* Platform */}
              <div>
                <label className={`block text-sm font-medium mb-2 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                  🖥️ Target Platform
                  <HelpTooltip text="Select which operating systems to build agents for. 'All' creates 3 binaries (Windows .exe, Linux, macOS)." />
                </label>
                <select
                  value={buildPlatform}
                  onChange={(e) => setBuildPlatform(e.target.value)}
                  disabled={building}
                  className={`w-full px-3 py-2 border rounded-lg ${darkMode ? 'bg-gray-700 border-gray-600 text-white' : 'border-gray-300'} ${building ? 'opacity-50 cursor-not-allowed' : ''}`}
                >
                  <option value="all">🌐 All Platforms (Windows, Linux, macOS) - 3 binaries</option>
                  <option value="windows">🪟 Windows Only (x64)</option>
                  <option value="linux">🐧 Linux Only (x64)</option>
                  <option value="darwin">🍎 macOS Only (x64)</option>
                </select>
                <p className={`text-xs mt-1 ${darkMode ? 'text-gray-500' : 'text-gray-500'}`}>
                  💡 Build time: ~30-60 seconds per platform
                </p>
              </div>

              {/* Staged vs Stageless */}
              <div className={`p-3 rounded ${darkMode ? 'bg-purple-900/20 border border-purple-700' : 'bg-purple-50 border border-purple-200'}`}>
                <h4 className={`text-sm font-semibold mb-3 ${darkMode ? 'text-purple-300' : 'text-purple-800'}`}>
                  📦 Payload Type
                  <HelpTooltip text="Staged: Small stager downloads full agent (better evasion). Stageless: All-in-one binary (simpler deployment).">
                    <FontAwesomeIcon icon={faInfoCircle} style={{ fontSize: '14px' }} />
                  </HelpTooltip>
                </h4>

                <div className="space-y-3">
                  {/* Staged/Stageless Toggle */}
                  <div>
                    <label className={`block text-xs font-medium mb-2 ${darkMode ? 'text-purple-200' : 'text-purple-700'}`}>
                      Deployment Strategy
                    </label>
                    <div className="grid grid-cols-2 gap-2">
                      <button
                        type="button"
                        onClick={() => setBuildConfig({ ...buildConfig, isStaged: true })}
                        disabled={building}
                        className={`px-3 py-2 rounded text-sm font-medium transition-colors ${
                          buildConfig.isStaged
                            ? 'bg-purple-600 text-white'
                            : darkMode
                            ? 'bg-gray-700 text-gray-300 hover:bg-gray-600'
                            : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                        } ${building ? 'opacity-50 cursor-not-allowed' : ''}`}
                      >
                        🎯 Staged
                        <div className="text-xs font-normal mt-1">5-10 KB stager</div>
                      </button>
                      <button
                        type="button"
                        onClick={() => setBuildConfig({ ...buildConfig, isStaged: false })}
                        disabled={building}
                        className={`px-3 py-2 rounded text-sm font-medium transition-colors ${
                          !buildConfig.isStaged
                            ? 'bg-purple-600 text-white'
                            : darkMode
                            ? 'bg-gray-700 text-gray-300 hover:bg-gray-600'
                            : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                        } ${building ? 'opacity-50 cursor-not-allowed' : ''}`}
                      >
                        📦 Stageless
                        <div className="text-xs font-normal mt-1">~7 MB all-in-one</div>
                      </button>
                    </div>
                    <p className={`text-xs mt-2 ${darkMode ? 'text-purple-300' : 'text-purple-600'}`}>
                      {buildConfig.isStaged
                        ? '✓ Recommended: Smaller initial payload, better evasion, flexible deployment'
                        : '⚠️ Larger file size but simpler deployment (no stage download required)'
                      }
                    </p>
                  </div>

                  {/* Stager Configuration (only visible for staged) */}
                  {buildConfig.isStaged && (
                    <>
                      <div>
                        <label className={`block text-xs font-medium mb-1 ${darkMode ? 'text-purple-200' : 'text-purple-700'}`}>
                          Stager Type (Windows only)
                          <HelpTooltip text="Stager format for initial compromise. Smaller = better for evasion." />
                        </label>
                        <select
                          value={buildConfig.stagerType}
                          onChange={(e) => setBuildConfig({ ...buildConfig, stagerType: e.target.value })}
                          disabled={building}
                          className={`w-full px-3 py-2 border rounded text-sm ${darkMode ? 'bg-gray-800 border-purple-600 text-white' : 'bg-white border-purple-300'}`}
                        >
                          <option value="exe">📄 EXE Stager (~5 KB) - Standalone executable</option>
                          <option value="dll">🔧 DLL Stager (~4 KB) - rundll32.exe compatible</option>
                          <option value="powershell">💻 PowerShell (~2 KB) - .ps1 script</option>
                          <option value="hta">🌐 HTA (~3 KB) - HTML Application (mshta.exe)</option>
                          <option value="vba">📊 VBA Macro (~2 KB) - Excel/Word macro</option>
                          <option value="jscript">📜 JScript (~2 KB) - .js file (wscript.exe)</option>
                        </select>
                        <p className={`text-xs mt-1 ${darkMode ? 'text-gray-500' : 'text-gray-500'}`}>
                          💡 PowerShell/VBA/JScript are smallest but require interpreter
                        </p>
                      </div>

                      <div>
                        <label className={`block text-xs font-medium mb-1 ${darkMode ? 'text-purple-200' : 'text-purple-700'}`}>
                          Stage Download Protocol
                          <HelpTooltip text="How the stager downloads the full agent. HTTPS is most reliable, DNS most covert." />
                        </label>
                        <select
                          value={buildConfig.stageProtocol}
                          onChange={(e) => setBuildConfig({ ...buildConfig, stageProtocol: e.target.value })}
                          disabled={building}
                          className={`w-full px-3 py-2 border rounded text-sm ${darkMode ? 'bg-gray-800 border-purple-600 text-white' : 'bg-white border-purple-300'}`}
                        >
                          <option value="https">🔒 HTTPS (Recommended) - Encrypted download</option>
                          <option value="http">⚠️ HTTP - Unencrypted (testing only)</option>
                          <option value="dns">🌐 DNS TXT Records - Covert exfiltration</option>
                          <option value="smb">💾 SMB Share - Internal network only</option>
                        </select>
                      </div>

                      <div>
                        <label className={`block text-xs font-medium mb-1 ${darkMode ? 'text-purple-200' : 'text-purple-700'}`}>
                          Stage Encryption
                          <HelpTooltip text="Encrypt the full agent during download. ChaCha20 recommended (fast, secure)." />
                        </label>
                        <select
                          value={buildConfig.stageEncryption}
                          onChange={(e) => setBuildConfig({ ...buildConfig, stageEncryption: e.target.value })}
                          disabled={building}
                          className={`w-full px-3 py-2 border rounded text-sm ${darkMode ? 'bg-gray-800 border-purple-600 text-white' : 'bg-white border-purple-300'}`}
                        >
                          <option value="chacha20">⚡ ChaCha20-Poly1305 (Recommended) - Fast & secure</option>
                          <option value="aes256">🔒 AES-256-GCM - Military-grade encryption</option>
                          <option value="xor">🔓 XOR Obfuscation - Faster but weaker</option>
                          <option value="none">⚠️ No Encryption - Testing only!</option>
                        </select>
                        <p className={`text-xs mt-1 ${darkMode ? 'text-gray-500' : 'text-gray-500'}`}>
                          ℹ️ ChaCha20 is used by Sliver C2 and WireGuard VPN
                        </p>
                      </div>

                      <div className={`p-2 rounded ${darkMode ? 'bg-purple-900/30' : 'bg-purple-100'}`}>
                        <p className={`text-xs ${darkMode ? 'text-purple-200' : 'text-purple-800'}`}>
                          <strong>📊 Staged Payload Size Estimate:</strong><br/>
                          • Stager: {buildConfig.stagerType === 'exe' || buildConfig.stagerType === 'dll' ? '4-5 KB' : '2-3 KB'}<br/>
                          • Full Agent: ~7 MB (downloaded on first run)<br/>
                          • Total Network Transfer: ~7 MB<br/>
                          • Disk Footprint: {buildConfig.stagerType === 'exe' || buildConfig.stagerType === 'dll' ? '~5 KB' : '~2 KB'} (stager only, agent runs in-memory)
                        </p>
                      </div>
                    </>
                  )}

                  {/* Stageless info */}
                  {!buildConfig.isStaged && (
                    <div className={`p-2 rounded ${darkMode ? 'bg-purple-900/30' : 'bg-purple-100'}`}>
                      <p className={`text-xs ${darkMode ? 'text-purple-200' : 'text-purple-800'}`}>
                        <strong>📦 Stageless Payload:</strong><br/>
                        • Single binary with all features embedded<br/>
                        • Size: ~7-8 MB depending on features<br/>
                        • No network download required on first run<br/>
                        • Simpler deployment but larger initial file<br/>
                        • Use for environments with egress filtering
                      </p>
                    </div>
                  )}
                </div>
              </div>

              {/* C2 Server Configuration */}
              <div className={`p-3 rounded ${darkMode ? 'bg-gray-900 border border-gray-700' : 'bg-gray-50 border border-gray-200'}`}>
                <h4 className={`text-sm font-semibold mb-3 ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                  🌐 C2 Server Configuration
                  <HelpTooltip text="Configure where agents will connect back to. This is embedded in the binary at compile time.">
                    <FontAwesomeIcon icon={faInfoCircle} style={{ fontSize: '14px' }} />
                  </HelpTooltip>
                </h4>

                <div className="grid grid-cols-2 gap-3">
                  <div className="col-span-2">
                    <label className={`block text-xs font-medium mb-1 ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                      C2 Server URL
                      <HelpTooltip text="The domain or IP where your C2 server is hosted. Use domain fronting or CDN for stealth." />
                    </label>
                    <input
                      type="text"
                      value={buildConfig.c2Url}
                      onChange={(e) => setBuildConfig({ ...buildConfig, c2Url: e.target.value })}
                      disabled={building}
                      placeholder="app.purplehq.ch (without protocol)"
                      className={`w-full px-3 py-2 border rounded text-sm ${darkMode ? 'bg-gray-800 border-gray-600 text-white' : 'border-gray-300'}`}
                    />
                  </div>

                  <div>
                    <label className={`block text-xs font-medium mb-1 ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                      Protocol
                      <HelpTooltip text="HTTPS is recommended for encryption. DNS for covert channels. SMB for internal pivoting." />
                    </label>
                    <select
                      value={buildConfig.protocol}
                      onChange={(e) => setBuildConfig({ ...buildConfig, protocol: e.target.value })}
                      disabled={building}
                      className={`w-full px-3 py-2 border rounded text-sm ${darkMode ? 'bg-gray-800 border-gray-600 text-white' : 'border-gray-300'}`}
                    >
                      <option value="https">🔒 HTTPS (Recommended)</option>
                      <option value="http">HTTP (Testing only)</option>
                      <option value="dns">🌐 DNS (Covert)</option>
                      <option value="smb">💾 SMB (Internal)</option>
                    </select>
                  </div>

                  <div>
                    <label className={`block text-xs font-medium mb-1 ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                      Port
                      <HelpTooltip text="Standard ports: HTTPS=443, HTTP=80, DNS=53. Custom ports may trigger alerts." />
                    </label>
                    <input
                      type="number"
                      value={buildConfig.c2Port}
                      onChange={(e) => setBuildConfig({ ...buildConfig, c2Port: parseInt(e.target.value) || 443 })}
                      disabled={building}
                      className={`w-full px-3 py-2 border rounded text-sm ${darkMode ? 'bg-gray-800 border-gray-600 text-white' : 'border-gray-300'}`}
                    />
                  </div>
                </div>
              </div>

              {/* Route Configuration */}
              <div className={`p-3 rounded ${darkMode ? 'bg-gray-900 border border-gray-700' : 'bg-gray-50 border border-gray-200'}`}>
                <h4 className={`text-sm font-semibold mb-3 ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                  🔀 Route Configuration (Optional)
                  <HelpTooltip text="Configure multi-hop routes through redirectors for enhanced OPSEC. Agents will use these routes to reach the C2 server.">
                    <FontAwesomeIcon icon={faInfoCircle} style={{ fontSize: '14px' }} />
                  </HelpTooltip>
                </h4>

                {selectedRoute ? (
                  <div className={`p-3 rounded ${darkMode ? 'bg-gray-800 border border-gray-600' : 'bg-blue-50 border border-blue-200'}`}>
                    <div className="flex justify-between items-start mb-2">
                      <div>
                        <strong className={darkMode ? 'text-white' : 'text-gray-900'}>{selectedRoute.name}</strong>
                        <div className={`text-xs mt-1 ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                          <div>
                            <FontAwesomeIcon icon={faRoute} className="mr-1" />
                            Primary: Agent → {selectedRoute.primaryRoute.hops.map(h => h.hostname || h.name).join(' → ')} → C2
                          </div>
                          <div className="mt-1">
                            Fallback Routes: {selectedRoute.fallbackRoutes.length} configured
                          </div>
                          <div className="mt-1">
                            Total Hops: {selectedRoute.primaryRoute.hops.length}
                          </div>
                        </div>
                      </div>
                      <button
                        onClick={() => {
                          setSelectedRoute(null);
                          setBuildConfig({ ...buildConfig, routeConfig: null });
                        }}
                        disabled={building}
                        className={`text-xs px-2 py-1 bg-red-600 text-white rounded hover:bg-red-700 ${building ? 'opacity-50 cursor-not-allowed' : ''}`}
                      >
                        Remove
                      </button>
                    </div>
                    <button
                      onClick={() => setShowRouteBuilder(true)}
                      disabled={building}
                      className={`text-xs px-3 py-1 bg-blue-600 text-white rounded hover:bg-blue-700 ${building ? 'opacity-50 cursor-not-allowed' : ''}`}
                    >
                      <FontAwesomeIcon icon={faRoute} className="mr-1" />
                      Edit Route
                    </button>
                  </div>
                ) : (
                  <div className="text-center">
                    <p className={`text-xs mb-2 ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                      No route configured. Agent will connect directly to C2 server.
                    </p>
                    <button
                      onClick={() => setShowRouteBuilder(true)}
                      disabled={building}
                      className={`px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 flex items-center gap-2 mx-auto ${building ? 'opacity-50 cursor-not-allowed' : ''}`}
                    >
                      <FontAwesomeIcon icon={faRoute} />
                      Configure Route
                    </button>
                    <p className={`text-xs mt-2 ${darkMode ? 'text-gray-500' : 'text-gray-500'}`}>
                      💡 Build routes through redirectors for enhanced stealth and resilience
                    </p>
                  </div>
                )}
              </div>

              {/* Communication Settings */}
              <div className={`p-3 rounded ${darkMode ? 'bg-gray-900 border border-gray-700' : 'bg-gray-50 border border-gray-200'}`}>
                <h4 className={`text-sm font-semibold mb-3 ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                  ⏱️ Communication Settings
                  <HelpTooltip text="Control how often agents check in and how they identify themselves to blend in with normal traffic">
                    <FontAwesomeIcon icon={faInfoCircle} style={{ fontSize: '14px' }} />
                  </HelpTooltip>
                </h4>

                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className={`block text-xs font-medium mb-1 ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                      Sleep Interval (seconds)
                      <HelpTooltip text="How long the agent waits between check-ins. Lower = more responsive but noisier. Higher = stealthier but slower." />
                    </label>
                    <input
                      type="number"
                      value={buildConfig.sleepInterval}
                      onChange={(e) => setBuildConfig({ ...buildConfig, sleepInterval: parseInt(e.target.value) || 60 })}
                      disabled={building}
                      min="1"
                      className={`w-full px-3 py-2 border rounded text-sm ${darkMode ? 'bg-gray-800 border-gray-600 text-white' : 'border-gray-300'}`}
                    />
                    <p className={`text-xs mt-1 ${darkMode ? 'text-gray-500' : 'text-gray-500'}`}>
                      💡 Recommended: 60-300s for operations, 10-30s for testing
                    </p>
                  </div>

                  <div>
                    <label className={`block text-xs font-medium mb-1 ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                      Jitter (%)
                      <HelpTooltip text="Randomizes sleep interval by ±X%. 30% jitter on 60s = 42-78s range. Defeats pattern detection." />
                    </label>
                    <input
                      type="number"
                      value={buildConfig.jitter}
                      onChange={(e) => setBuildConfig({ ...buildConfig, jitter: parseInt(e.target.value) || 0 })}
                      disabled={building}
                      min="0"
                      max="100"
                      className={`w-full px-3 py-2 border rounded text-sm ${darkMode ? 'bg-gray-800 border-gray-600 text-white' : 'border-gray-300'}`}
                    />
                    <p className={`text-xs mt-1 ${darkMode ? 'text-gray-500' : 'text-gray-500'}`}>
                      💡 Recommended: 30-50% to avoid beacon analysis
                    </p>
                  </div>

                  <div className="col-span-2">
                    <label className={`block text-xs font-medium mb-1 ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                      User-Agent String
                      <HelpTooltip text="HTTP User-Agent header. Use common browser strings to blend in. Outdated strings may trigger alerts." />
                    </label>
                    <input
                      type="text"
                      value={buildConfig.userAgent}
                      onChange={(e) => setBuildConfig({ ...buildConfig, userAgent: e.target.value })}
                      disabled={building}
                      className={`w-full px-3 py-2 border rounded text-sm ${darkMode ? 'bg-gray-800 border-gray-600 text-white' : 'border-gray-300'}`}
                    />
                    <p className={`text-xs mt-1 ${darkMode ? 'text-gray-500' : 'text-gray-500'}`}>
                      💡 Default mimics Chrome on Windows 10 - update regularly for current browser versions
                    </p>
                  </div>
                </div>
              </div>

              {/* Security & Evasion */}
              <div className={`p-3 rounded ${darkMode ? 'bg-gray-900 border border-gray-700' : 'bg-gray-50 border border-gray-200'}`}>
                <h4 className={`text-sm font-semibold mb-3 ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                  🛡️ Security & Evasion
                  <HelpTooltip text="Configure encryption and evasion techniques. More features = larger binary but better detection avoidance">
                    <FontAwesomeIcon icon={faInfoCircle} style={{ fontSize: '14px' }} />
                  </HelpTooltip>
                </h4>

                <div className="space-y-3">
                  <div>
                    <label className={`block text-xs font-medium mb-1 ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                      Encryption
                      <HelpTooltip text="All C2 traffic encryption. AES-256 is military-grade. ChaCha20 is faster on mobile. Never use 'None' in production!" />
                    </label>
                    <select
                      value={buildConfig.encryption}
                      onChange={(e) => setBuildConfig({ ...buildConfig, encryption: e.target.value })}
                      disabled={building}
                      className={`w-full px-3 py-2 border rounded text-sm ${darkMode ? 'bg-gray-800 border-gray-600 text-white' : 'border-gray-300'}`}
                    >
                      <option value="aes256">🔒 AES-256-GCM (Recommended)</option>
                      <option value="aes128">AES-128-GCM (Faster)</option>
                      <option value="chacha20">ChaCha20-Poly1305 (Mobile)</option>
                      <option value="none">⚠️ None (Testing ONLY)</option>
                    </select>
                  </div>

                  <div>
                    <label className={`flex items-center cursor-pointer ${building ? 'opacity-50 cursor-not-allowed' : ''}`}>
                      <input
                        type="checkbox"
                        checked={buildConfig.obfuscation}
                        onChange={(e) => setBuildConfig({ ...buildConfig, obfuscation: e.target.checked })}
                        disabled={building}
                        className="mr-2"
                      />
                      <span className={`text-xs ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                        Enable Code Obfuscation
                        <HelpTooltip text="Encrypts strings, mangles function names, and obscures control flow. Increases binary size by ~20%." />
                      </span>
                    </label>
                    <p className={`text-xs ml-6 ${darkMode ? 'text-gray-500' : 'text-gray-500'}`}>
                      Harder to reverse engineer, defeats basic static analysis
                    </p>
                  </div>

                  <div>
                    <label className={`flex items-center cursor-pointer ${building ? 'opacity-50 cursor-not-allowed' : ''}`}>
                      <input
                        type="checkbox"
                        checked={buildConfig.antiDebug}
                        onChange={(e) => setBuildConfig({ ...buildConfig, antiDebug: e.target.checked })}
                        disabled={building}
                        className="mr-2"
                      />
                      <span className={`text-xs ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                        Anti-Debug Techniques
                        <HelpTooltip text="Detects debuggers (OllyDbg, x64dbg, WinDbg) and exits. Makes dynamic analysis harder." />
                      </span>
                    </label>
                    <p className={`text-xs ml-6 ${darkMode ? 'text-gray-500' : 'text-gray-500'}`}>
                      Checks for IsDebuggerPresent, NtQueryInformationProcess
                    </p>
                  </div>

                  <div>
                    <label className={`flex items-center cursor-pointer ${building ? 'opacity-50 cursor-not-allowed' : ''}`}>
                      <input
                        type="checkbox"
                        checked={buildConfig.sandbox}
                        onChange={(e) => setBuildConfig({ ...buildConfig, sandbox: e.target.checked })}
                        disabled={building}
                        className="mr-2"
                      />
                      <span className={`text-xs ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                        Sandbox Detection
                        <HelpTooltip text="Detects VMs (VMware, VirtualBox, Hyper-V) and sandboxes (Cuckoo, Joe Sandbox). Agent exits if detected." />
                      </span>
                    </label>
                    <p className={`text-xs ml-6 ${darkMode ? 'text-gray-500' : 'text-gray-500'}`}>
                      Checks for VM artifacts, low resources, analysis tools
                    </p>
                  </div>

                  <div>
                    <label className={`block text-xs font-medium mb-1 ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                      Kill Date (Optional)
                      <HelpTooltip text="Agent automatically exits after this date. Prevents agents from running after engagement ends. Leave empty for no expiration." />
                    </label>
                    <input
                      type="date"
                      value={buildConfig.killDate}
                      onChange={(e) => setBuildConfig({ ...buildConfig, killDate: e.target.value })}
                      disabled={building}
                      className={`w-full px-3 py-2 border rounded text-sm ${darkMode ? 'bg-gray-800 border-gray-600 text-white' : 'border-gray-300'}`}
                    />
                    <p className={`text-xs mt-1 ${darkMode ? 'text-gray-500' : 'text-gray-500'}`}>
                      💡 Set to engagement end date + 1 week for cleanup
                    </p>
                  </div>
                </div>
              </div>

              {/* Compiled Modules — controls what offensive capabilities the binary includes */}
              <div className={`p-3 rounded ${darkMode ? 'bg-gray-900 border border-gray-700' : 'bg-gray-50 border border-gray-200'}`}>
                <h4 className={`text-sm font-semibold mb-3 ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                  🧩 Compiled Modules
                  <HelpTooltip text="Select which capabilities to compile into the binary. Modules marked ⚠️ will trigger AV/EDR detection. A 'safe' build with no modules selected runs without AV flags.">
                    <FontAwesomeIcon icon={faInfoCircle} style={{ fontSize: '14px' }} />
                  </HelpTooltip>
                </h4>

                <div className={`text-xs mb-3 p-2 rounded ${darkMode ? 'bg-yellow-900/20 text-yellow-400 border border-yellow-800' : 'bg-yellow-50 text-yellow-700 border border-yellow-200'}`}>
                  💡 <strong>Safe build</strong> (no modules): shell, file ops, screenshots, process list, network scan, sysinfo — no AV detection.
                  Modules marked <span className="text-red-400 font-bold">⚠️</span> add offensive capabilities that <strong>will trigger Windows Defender</strong> and most EDR products.
                </div>

                <div className="space-y-2">
                  {[
                    { key: 'modEvasion', label: 'AMSI & ETW Bypass', icon: '⚠️', desc: 'Memory-patches AmsiScanBuffer and EtwEventWrite to disable AV scanning and EDR telemetry', unsafe: true },
                    { key: 'modCredentials', label: 'Credential Harvesting', icon: '⚠️', desc: 'Mimikatz, LSASS dump, DCSync, Kerberoast, Chrome & WiFi password extraction', unsafe: true },
                    { key: 'modLateral', label: 'Lateral Movement', icon: '⚠️', desc: 'PSExec, WMI exec, scheduled tasks, service exec — remote code execution on other hosts', unsafe: true },
                    { key: 'modInjection', label: 'Process Injection', icon: '⚠️', desc: 'CreateRemoteThread, QueueUserAPC, NtQueueApcThread — inject code into other processes', unsafe: true },
                    { key: 'modKeylogger', label: 'Keylogger', icon: '⚠️', desc: 'Keyboard hooking via SetWindowsHookEx (Windows) or /dev/input (Linux)', unsafe: true }
                  ].map(mod => (
                    <div key={mod.key} className={`flex items-start p-2 rounded ${buildConfig[mod.key] ? (darkMode ? 'bg-red-900/20 border border-red-800/40' : 'bg-red-50 border border-red-200') : ''}`}>
                      <label className={`flex items-start cursor-pointer w-full ${building ? 'opacity-50 cursor-not-allowed' : ''}`}>
                        <input
                          type="checkbox"
                          checked={buildConfig[mod.key]}
                          onChange={(e) => setBuildConfig({ ...buildConfig, [mod.key]: e.target.checked })}
                          disabled={building}
                          className="mr-2 mt-0.5"
                        />
                        <div>
                          <span className={`text-xs font-medium ${darkMode ? 'text-gray-200' : 'text-gray-800'}`}>
                            {mod.icon} {mod.label}
                          </span>
                          <p className={`text-xs mt-0.5 ${darkMode ? 'text-gray-500' : 'text-gray-500'}`}>
                            {mod.desc}
                          </p>
                        </div>
                      </label>
                    </div>
                  ))}
                </div>

                <div className="flex gap-2 mt-3">
                  <button
                    type="button"
                    onClick={() => setBuildConfig(prev => ({ ...prev, modEvasion: true, modCredentials: true, modLateral: true, modInjection: true, modKeylogger: true }))}
                    disabled={building}
                    className={`px-2 py-1 rounded text-xs ${darkMode ? 'bg-red-900/30 text-red-400 hover:bg-red-900/50' : 'bg-red-50 text-red-600 hover:bg-red-100'}`}
                  >
                    ⚠️ Select All (Offensive)
                  </button>
                  <button
                    type="button"
                    onClick={() => setBuildConfig(prev => ({ ...prev, modEvasion: false, modCredentials: false, modLateral: false, modInjection: false, modKeylogger: false }))}
                    disabled={building}
                    className={`px-2 py-1 rounded text-xs ${darkMode ? 'bg-green-900/30 text-green-400 hover:bg-green-900/50' : 'bg-green-50 text-green-600 hover:bg-green-100'}`}
                  >
                    ✅ Clear All (Safe)
                  </button>
                </div>
              </div>

              {/* Advanced Evasion */}
              <div className={`p-3 rounded ${darkMode ? 'bg-gray-900 border border-gray-700' : 'bg-gray-50 border border-gray-200'}`}>
                <h4 className={`text-sm font-semibold mb-3 ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                  🥷 Advanced Evasion (Windows)
                  <HelpTooltip text="Advanced techniques for bypassing EDR and AV detection. Windows-only features will be ignored on Linux/macOS builds.">
                    <FontAwesomeIcon icon={faInfoCircle} style={{ fontSize: '14px' }} />
                  </HelpTooltip>
                </h4>

                <div className="space-y-3">
                  <div>
                    <label className={`flex items-center cursor-pointer ${building ? 'opacity-50 cursor-not-allowed' : ''}`}>
                      <input
                        type="checkbox"
                        checked={buildConfig.autoAMSI}
                        onChange={(e) => setBuildConfig({ ...buildConfig, autoAMSI: e.target.checked })}
                        disabled={building}
                        className="mr-2"
                      />
                      <span className={`text-xs ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                        Auto-Patch AMSI on Startup
                        <HelpTooltip text="Automatically patch AmsiScanBuffer when agent starts. Disables PowerShell/script AV scanning." />
                      </span>
                    </label>
                    <p className={`text-xs ml-6 ${darkMode ? 'text-gray-500' : 'text-gray-500'}`}>
                      Bypasses Windows Antimalware Scan Interface (AMSI)
                    </p>
                  </div>

                  <div>
                    <label className={`flex items-center cursor-pointer ${building ? 'opacity-50 cursor-not-allowed' : ''}`}>
                      <input
                        type="checkbox"
                        checked={buildConfig.autoETW}
                        onChange={(e) => setBuildConfig({ ...buildConfig, autoETW: e.target.checked })}
                        disabled={building}
                        className="mr-2"
                      />
                      <span className={`text-xs ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                        Auto-Patch ETW on Startup
                        <HelpTooltip text="Automatically patch EtwEventWrite when agent starts. Blinds EDR telemetry collection." />
                      </span>
                    </label>
                    <p className={`text-xs ml-6 ${darkMode ? 'text-gray-500' : 'text-gray-500'}`}>
                      Disables Event Tracing for Windows (ETW) - reduces EDR visibility
                    </p>
                  </div>

                  <div>
                    <label className={`block text-xs font-medium mb-1 ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                      Process Injection Method (Windows)
                      <HelpTooltip text="Choose how to inject code into remote processes. CreateRemoteThread is classic but heavily monitored. QueueUserAPC/NtQueueApcThread are stealthier." />
                    </label>
                    <select
                      value={buildConfig.injectionMethod}
                      onChange={(e) => setBuildConfig({ ...buildConfig, injectionMethod: e.target.value })}
                      disabled={building}
                      className={`w-full px-3 py-2 border rounded text-sm ${darkMode ? 'bg-gray-800 border-gray-600 text-white' : 'border-gray-300'}`}
                    >
                      <option value="createRemoteThread">CreateRemoteThread (Classic, High Detection)</option>
                      <option value="queueUserAPC">QueueUserAPC (Stealthier)</option>
                      <option value="ntQueueApcThread">NtQueueApcThread (Bypasses hooks)</option>
                      <option value="selfProcess">Self-Process (Safest, no cross-process)</option>
                    </select>
                    <p className={`text-xs mt-1 ${darkMode ? 'text-gray-500' : 'text-gray-500'}`}>
                      💡 NtQueueApcThread bypasses kernel32 hooks, recommended for EDR evasion
                    </p>
                  </div>

                  <div>
                    <label className={`flex items-center cursor-pointer ${building ? 'opacity-50 cursor-not-allowed' : ''}`}>
                      <input
                        type="checkbox"
                        checked={buildConfig.sleepObfuscation}
                        onChange={(e) => setBuildConfig({ ...buildConfig, sleepObfuscation: e.target.checked })}
                        disabled={building}
                        className="mr-2"
                      />
                      <span className={`text-xs ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                        Sleep Obfuscation
                        <HelpTooltip text="Encrypts agent memory during sleep to evade memory scanners. Increases CPU but much stealthier." />
                      </span>
                    </label>
                    <p className={`text-xs ml-6 ${darkMode ? 'text-gray-500' : 'text-gray-500'}`}>
                      Beacon memory encrypted while sleeping - defeats memory scanners
                    </p>
                  </div>

                  <div>
                    <label className={`flex items-center cursor-pointer ${building ? 'opacity-50 cursor-not-allowed' : ''}`}>
                      <input
                        type="checkbox"
                        checked={buildConfig.syscalls}
                        onChange={(e) => setBuildConfig({ ...buildConfig, syscalls: e.target.checked })}
                        disabled={building}
                        className="mr-2"
                      />
                      <span className={`text-xs ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                        Direct Syscalls
                        <HelpTooltip text="Use direct syscalls instead of Windows API. Bypasses userland hooks from EDR. Advanced technique." />
                      </span>
                    </label>
                    <p className={`text-xs ml-6 ${darkMode ? 'text-gray-500' : 'text-gray-500'}`}>
                      Bypasses API hooks by calling ntdll/kernel directly
                    </p>
                  </div>
                </div>
              </div>

              {/* Working Hours */}
              <div className={`p-3 rounded ${darkMode ? 'bg-gray-900 border border-gray-700' : 'bg-gray-50 border border-gray-200'}`}>
                <h4 className={`text-sm font-semibold mb-3 ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                  ⏰ Working Hours
                  <HelpTooltip text="Limit when the agent is active to blend in with business hours and reduce detection risk">
                    <FontAwesomeIcon icon={faInfoCircle} style={{ fontSize: '14px' }} />
                  </HelpTooltip>
                </h4>

                <div className="space-y-3">
                  <div>
                    <label className={`flex items-center cursor-pointer ${building ? 'opacity-50 cursor-not-allowed' : ''}`}>
                      <input
                        type="checkbox"
                        checked={buildConfig.workingHoursEnabled}
                        onChange={(e) => setBuildConfig({ ...buildConfig, workingHoursEnabled: e.target.checked })}
                        disabled={building}
                        className="mr-2"
                      />
                      <span className={`text-xs ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                        Enable Working Hours Restriction
                        <HelpTooltip text="Agent only checks in during specified hours (local time on target). Sleeps silently outside these hours." />
                      </span>
                    </label>
                    <p className={`text-xs ml-6 ${darkMode ? 'text-gray-500' : 'text-gray-500'}`}>
                      Agent goes dormant outside business hours to appear as normal user activity
                    </p>
                  </div>

                  {buildConfig.workingHoursEnabled && (
                    <div className="grid grid-cols-2 gap-3 ml-6">
                      <div>
                        <label className={`block text-xs font-medium mb-1 ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                          Start Hour (24h format)
                        </label>
                        <input
                          type="number"
                          min="0"
                          max="23"
                          value={buildConfig.workingHoursStart}
                          onChange={(e) => setBuildConfig({ ...buildConfig, workingHoursStart: parseInt(e.target.value) || 0 })}
                          disabled={building}
                          className={`w-full px-3 py-2 border rounded text-sm ${darkMode ? 'bg-gray-800 border-gray-600 text-white' : 'border-gray-300'}`}
                        />
                        <p className={`text-xs mt-1 ${darkMode ? 'text-gray-500' : 'text-gray-500'}`}>
                          e.g., 9 for 9:00 AM
                        </p>
                      </div>

                      <div>
                        <label className={`block text-xs font-medium mb-1 ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                          End Hour (24h format)
                        </label>
                        <input
                          type="number"
                          min="0"
                          max="23"
                          value={buildConfig.workingHoursEnd}
                          onChange={(e) => setBuildConfig({ ...buildConfig, workingHoursEnd: parseInt(e.target.value) || 17 })}
                          disabled={building}
                          className={`w-full px-3 py-2 border rounded text-sm ${darkMode ? 'bg-gray-800 border-gray-600 text-white' : 'border-gray-300'}`}
                        />
                        <p className={`text-xs mt-1 ${darkMode ? 'text-gray-500' : 'text-gray-500'}`}>
                          e.g., 17 for 5:00 PM
                        </p>
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {buildOutput && (
                <div>
                  <label className={`block text-sm font-medium mb-2 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                    Build Output
                  </label>
                  <pre className={`text-xs p-3 rounded font-mono overflow-x-auto h-48 overflow-y-auto ${darkMode ? 'bg-gray-900 text-gray-300' : 'bg-gray-100 text-gray-700'}`}>
                    {buildOutput}
                  </pre>
                </div>
              )}

              {/* Build Summary */}
              <div className={`p-4 rounded border ${darkMode ? 'bg-blue-900/20 border-blue-800' : 'bg-blue-50 border-blue-200'}`}>
                <h4 className={`text-sm font-semibold mb-2 ${darkMode ? 'text-blue-300' : 'text-blue-800'}`}>
                  📋 Build Summary
                </h4>
                <div className={`text-xs space-y-1 ${darkMode ? 'text-blue-400' : 'text-blue-700'}`}>
                  <div><strong>Name:</strong> {buildConfig.name || '(required)'}</div>
                  <div><strong>C2 Server:</strong> {buildConfig.protocol}://{buildConfig.c2Url}:{buildConfig.c2Port}</div>
                  <div><strong>Check-in:</strong> Every {buildConfig.sleepInterval}s (±{buildConfig.jitter}% jitter)</div>
                  {buildConfig.workingHoursEnabled && (
                    <div><strong>Working Hours:</strong> {buildConfig.workingHoursStart}:00 - {buildConfig.workingHoursEnd}:00 (local time)</div>
                  )}
                  <div><strong>Platform:</strong> {buildPlatform === 'all' ? 'Windows + Linux + macOS' : buildPlatform}</div>
                  <div><strong>Payload Type:</strong> {buildConfig.isStaged ? `📦 Staged (${buildConfig.stagerType.toUpperCase()} stager via ${buildConfig.stageProtocol.toUpperCase()})` : '📦 Stageless (all-in-one)'}</div>
                  {buildConfig.isStaged && (
                    <div><strong>Stage Encryption:</strong> {buildConfig.stageEncryption.toUpperCase()}</div>
                  )}
                  <div><strong>C2 Encryption:</strong> {buildConfig.encryption.toUpperCase()}</div>
                  {selectedRoute && (
                    <div><strong>Route:</strong> {selectedRoute.name} ({selectedRoute.primaryRoute.hops.length} hops, {selectedRoute.fallbackRoutes.length} fallbacks)</div>
                  )}
                  <div><strong>Features:</strong>
                    {buildConfig.obfuscation && ' Obfuscation'}
                    {buildConfig.antiDebug && ' • Anti-Debug'}
                    {buildConfig.sandbox && ' • Sandbox Detection'}
                    {buildConfig.killDate && ` • Expires ${buildConfig.killDate}`}
                  </div>
                  <div><strong>Advanced Evasion (Windows):</strong>
                    {buildConfig.autoAMSI && ' Auto-AMSI'}
                    {buildConfig.autoETW && ' • Auto-ETW'}
                    {buildConfig.sleepObfuscation && ' • Sleep Obfuscation'}
                    {buildConfig.syscalls && ' • Direct Syscalls'}
                    {buildConfig.injectionMethod !== 'createRemoteThread' && ` • Injection: ${buildConfig.injectionMethod}`}
                    {!buildConfig.autoAMSI && !buildConfig.autoETW && !buildConfig.sleepObfuscation && !buildConfig.syscalls && buildConfig.injectionMethod === 'createRemoteThread' && ' None'}
                  </div>
                  <div className="mt-2 pt-2 border-t border-blue-700/30">
                    <strong>⏱️ Estimated build time:</strong> {buildPlatform === 'all' ? '90-120' : '30-60'} seconds
                  </div>
                </div>
              </div>
            </div>

            <div className="flex items-center justify-end gap-3 mt-6">
              <button
                onClick={() => setShowBuildModal(false)}
                disabled={building}
                className={`px-4 py-2 rounded ${darkMode ? 'bg-gray-700 text-gray-300 hover:bg-gray-600' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'} ${building ? 'opacity-50 cursor-not-allowed' : ''}`}
              >
                {building ? 'Building...' : 'Cancel'}
              </button>
              <button
                onClick={handleBuildPayloads}
                disabled={building || !buildConfig.name.trim()}
                className={`px-4 py-2 bg-green-600 text-white rounded hover:bg-green-700 flex items-center gap-2 ${(building || !buildConfig.name.trim()) ? 'opacity-50 cursor-not-allowed' : ''}`}
                title={!buildConfig.name.trim() ? 'Agent name is required' : ''}
              >
                {building ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                    Building...
                  </>
                ) : (
                  <>
                    <FontAwesomeIcon icon={faBox} />
                    Build Payloads
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Route Builder Modal — z-[60] to stack above the build modal (z-50) */}
      {showRouteBuilder && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4" style={{ zIndex: 60 }}>
          <div className={`rounded-lg shadow-xl max-w-7xl w-full max-h-[90vh] overflow-y-auto ${darkMode ? 'bg-gray-800' : 'bg-white'}`}>
            <div className={`sticky top-0 z-10 flex items-center justify-between p-4 border-b ${darkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'}`}>
              <h3 className={`text-lg font-semibold ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                <FontAwesomeIcon icon={faRoute} className="mr-2" />
                Route Builder
              </h3>
              <button
                onClick={() => setShowRouteBuilder(false)}
                className={`text-2xl leading-none px-2 py-1 rounded ${darkMode ? 'text-gray-400 hover:text-white hover:bg-gray-700' : 'text-gray-600 hover:text-black hover:bg-gray-200'}`}
              >
                ×
              </button>
            </div>
            <div className="p-4">
              <RouteBuilder
                projectId={null}
                onSave={(routeConfig) => {
                  setSelectedRoute(routeConfig);
                  setBuildConfig({ ...buildConfig, routeConfig });
                  setShowRouteBuilder(false);
                }}
                onCancel={() => setShowRouteBuilder(false)}
                existingRoute={selectedRoute}
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default StagerBuilder;
